import React from 'react';
import {
    selectCapsuleSymbolBindingFromState,
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';

import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';

import { ObvTileDataStateTimeSeriesItem } from 'widgets/obvTile/store/stream/ObvTileStreamState';
import {
    initialObvTileConfigState,
    ObvTileConfigState,
} from 'widgets/obvTile/store/config/ObvTileConfigState';
import {
    buildObvTileData,
    ObvTileDataTimeSeriesItem,
} from 'widgets/obvTile/obvTileData';
import { toObvRequestUid } from 'widgets/obvTile/api/ObvTileStreamRequest';

export interface WidgetPanelObvTileProps extends WidgetPanelDetailProps {
    mode: 'timeSeries' | 'data' | 'chartTimeSeries';
}

export default function WidgetPanelObvTile(props: WidgetPanelObvTileProps) {
    const {
        widgetState,
        mode,
        widget: { id: widgetKey },
        state,
    } = props;

    const config =
        widgetState?.config ??
        (initialObvTileConfigState as ObvTileConfigState);

    const symbolBinding = selectCapsuleSymbolBindingFromState(props);

    if (!symbolBinding) {
        return <></>;
    }

    const { symbol, source } = symbolBinding;

    const requestUid = toObvRequestUid(symbol, config);
    const data = state.state.streams.data.obvTiles[requestUid]?.data;

    const obvTileData = buildObvTileData(config.bucketSize, data);

    if (data) {
        for (const b of data.timeSeries) {
            const withTime = b as unknown as { time: number };
            withTime.time = b._i * 60000;
            showNiceDate(withTime, 'time');
        }
    }

    if (obvTileData) {
        for (const t of obvTileData.timeSeries) {
            showNiceDate(t, 'time');
            showNiceDate(t, 'startTime');
            showNiceDate(t, 'endTime');
        }
        showNiceDate(obvTileData, 'startTime');
        showNiceDate(obvTileData, 'endTime');
    }

    if (mode === 'timeSeries') {
        return data ? (
            <WidgetPanelGrid<ObvTileDataStateTimeSeriesItem>
                key="timeSeriesGrid"
                data={data!.timeSeries}
                getRowDataId={(d) => d._i.toString()}
                csvFileName={`${widgetKey}-timeSeries`}
            />
        ) : (
            <></>
        );
    }

    if (mode === 'chartTimeSeries') {
        return obvTileData ? (
            <WidgetPanelGrid<ObvTileDataTimeSeriesItem>
                key="chartTimeSeriesGrid"
                data={obvTileData.timeSeries}
                getRowDataId={(d) => d.time.toString()}
                csvFileName={`${widgetKey}-data`}
                colDefs={getChartColDefs()}
            />
        ) : (
            <></>
        );
    }

    return (
        <div className="widget-panel">
            <div className="title">Obv Capsule Data</div>
            <div className="field">
                <span className={'label'}>Symbol:</span>
                {symbol}
            </div>
            <div className="field">
                <span className={'label'}>Symbol Source</span>
                {source}
            </div>
            <div className="field">
                <span className="label">Request Uid:</span>
                {requestUid}
            </div>
            <JsonPanel data={obvTileData} />
        </div>
    );
}

export function addObvTilePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const renderData = () => (
        <WidgetPanelObvTile {...detailProps} mode="data" />
    );
    panes.push({
        menuItem: 'Data',
        render: renderData,
    });

    const renderTs = () => (
        <WidgetPanelObvTile {...detailProps} mode="timeSeries" />
    );
    panes.push({
        menuItem: 'Time Series',
        render: renderTs,
    });
    const render = () => (
        <WidgetPanelObvTile {...detailProps} mode="chartTimeSeries" />
    );
    panes.push({
        menuItem: 'Chart Time Series',
        render,
    });
}

function getChartColDefs(): ColDef[] {
    return [
        'time',
        'timeDisplay',
        'obv',
        'pos-obv',
        'neg-obv',
        'startTime',
        'startTimeDisplay',
        'endTime',
        'endTimeDisplay',
    ].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));
}
